import { get, flowRight } from 'lodash';
import PropTypes from 'prop-types';
import React, { Component } from 'react';
import classNames from 'classnames';
import Editor, { composeDecorators } from 'draft-js-plugins-editor';
import createDndPlugin from 'draft-js-drag-n-drop-plugin';
import decorateComponentWithProps from 'decorate-component-with-props';
import TextEditorImage from './text-editor-image';
import TextEditorVideoEmbed from './text-editor-video-embed';
import TextEditorHashtag from './text-editor-hashtag';
import { EXPERIMENT_CODE_BLOCK } from '../../constants/experiments';
import { APP_TEXT_COLOR_PATH } from '../../constants/wix-params';
import withSettingsColor from '../../hoc/with-settings-color';
import withExperiment from '../../hoc/with-experiment';
import createImagePlugin from './plugins/image';
import createVideoEmbedPlugin from './plugins/video-embed';
import createMediaBlockPlugin from './plugins/media-block';
import createCodeBlockPlugin from './plugins/code-block';
import createHashtagPlugin from './plugins/hashtag';
import { createEditorState, cleanUpContent } from './modifiers/text-editor-utils';
import createTextEditorDecorator from './decorators/create-text-editor-decorator';
import createBlockRenderMap from './utils/create-block-render-map';

class DraftRenderer extends Component {
  state = {};

  constructor(props) {
    super(props);

    const atomicBlockPlugin = createMediaBlockPlugin({
      theme: get(props.theme, 'mediaBlock'),
      ...get(props.config, 'mediaBlock'),
    });
    this.MediaBlockEditor = atomicBlockPlugin.MediaBlockEditor;

    const dndPlugin = createDndPlugin();

    const imagePlugin = createImagePlugin({
      Image: decorateComponentWithProps(TextEditorImage, {
        isPublic: props.isPublic,
      }),
      decorator: composeDecorators(atomicBlockPlugin.decorator, dndPlugin.decorator),
    });

    const videoEmbedPlugin = createVideoEmbedPlugin({
      VideoEmbed: TextEditorVideoEmbed,
      decorator: composeDecorators(atomicBlockPlugin.decorator, dndPlugin.decorator),
    });

    this.plugins = [atomicBlockPlugin, dndPlugin, imagePlugin, videoEmbedPlugin];
    this.blockRenderMap = createBlockRenderMap({ titleTheme: get(props.theme, 'title') });

    if (props.isCodeBlockEnabled) {
      this.plugins.push(createCodeBlockPlugin({ backgroundColor: props.codeBackgroundColor }));
    }

    if (props.enableHashtags) {
      this.plugins.push(
        createHashtagPlugin({
          Hashtag: decorateComponentWithProps(TextEditorHashtag, {
            createHref: decoratedText => `/search/posts?query=${encodeURIComponent('#')}${decoratedText}`,
            theme: get(props.theme, 'hashtag'),
          }),
        })
      );
    }

    this.decorators = [createTextEditorDecorator({ theme: props.theme, followLinks: props.followLinks })];

    this.state = {
      editorState: createEditorState(cleanUpContent(props.content)),
    };
  }

  componentWillReceiveProps({ content }) {
    if (content !== this.props.content) {
      this.updateContent(content);
    }
  }

  updateContent(content) {
    const decorator = this.state.editorState.getDecorator();
    this.setState({
      editorState: createEditorState(cleanUpContent(content), decorator),
    });
  }

  handleChange = editorState =>
    this.setState({
      editorState,
    });

  render() {
    const { theme, type } = this.props;
    const containerClassName = classNames(get(theme, 'container'), get(theme, `types.${type}`));

    return (
      <div className={containerClassName}>
        <Editor
          onChange={this.handleChange}
          editorState={this.state.editorState}
          blockStyleFn={get(theme, 'blockStyleFn')}
          blockRenderMap={this.blockRenderMap}
          plugins={this.plugins}
          decorators={this.decorators}
          readOnly
        />
      </div>
    );
  }
}

DraftRenderer.propTypes = {
  content: PropTypes.object.isRequired,
  type: PropTypes.string,
  identifier: PropTypes.string,
  isPublic: PropTypes.bool,
  isCodeBlockEnabled: PropTypes.bool,
  enableHashtags: PropTypes.bool,
  codeBackgroundColor: PropTypes.string,
  theme: PropTypes.object,
  followLinks: PropTypes.bool,
  config: PropTypes.object,
};

export default flowRight(
  withExperiment({
    isCodeBlockEnabled: EXPERIMENT_CODE_BLOCK,
  }),
  withSettingsColor(APP_TEXT_COLOR_PATH, 'codeBackgroundColor', 'color-5', 0.1)
)(DraftRenderer);



// WEBPACK FOOTER //
// ./app/components/text-editor/draft-renderer.js